import { useCallback } from "react"
import { logger } from "shared-ts/logger"

import { toast } from "./toast"

export interface UseCopyToClipboardProps {
  subject?: string
  shouldToast?: boolean
}

function capitalize(str: string) {
  if (!str) return str
  return str[0].toUpperCase() + str.slice(1).toLowerCase()
}

/**
 * Hook for copying text to clipboard with opt-out toast. Resolves whether the
 * write succeeded, so callers that confirm in their own UI can stay in step
 * with the toast rather than reporting success the clipboard never gave them.
 */
export function useCopyToClipboard({
  subject,
  shouldToast = true,
}: UseCopyToClipboardProps = {}) {
  const copy = useCallback(
    async (toCopy: string): Promise<boolean> => {
      if (!toCopy) {
        logger.error("Copying empty string to clipboard")
      }

      try {
        await navigator.clipboard.writeText(toCopy)
        if (shouldToast) {
          const successMessage = subject
            ? `${capitalize(subject)} copied`
            : "Copied to clipboard"
          toast.success({ message: successMessage })
        }
        return true
      } catch (e: unknown) {
        if (shouldToast) {
          const errorMessage = subject
            ? `Failed to copy ${subject.toLowerCase()}`
            : "Failed to copy"
          toast.error({ message: errorMessage, error: e })
        }
        return false
      }
    },
    [subject, shouldToast]
  )

  return copy
}
